// @flow

import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { colors, fonts } from 'shared/styleguide';
import { Formik, Form, FieldGroup, FieldGroupRow } from 'shared/components/form';
import AddressAutocomplete from 'main-app/components/AddressAutocomplete';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'shared/components/modal';
import Button from 'shared/components/common/Button';
import * as Actions from 'main-app/store/Actions';
import * as Yup from 'yup';
import { getStripe } from 'shared/utils/stripe';

const CARD_ELEMENT_STYLE = {
  base: {
    fontSize: '14px',
    fontFamily: '"Source Sans Pro", -apple-system, sans-serif',
    color: '#2D2D2D',
    '::placeholder': { color: '#a8acbb' },
  },
  invalid: { color: '#de002e' },
};

const StripeFieldWrapper = styled.div`
  border: 1px solid ${colors.lightBlueGrey};
  border-radius: 5px;
  padding: 8px 12px;
  min-height: 34px;
  background: ${colors.white};

  &:focus-within {
    border-color: ${colors.azul};
  }

  .StripeElement {
    width: 100%;
  }
`;

const FieldLabel = styled.div`
  ${fonts.smallBold};
  color: ${colors.battleshipGrey};
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const StripeField = styled.div`
  margin-bottom: 16px;
`;

const validationSchema = Yup.object().shape({
  fullName: Yup.string().required('Name on card is required'),
  addressLine1: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zip: Yup.string().required('Zip code is required'),
  country: Yup.string().required('Country is required'),
});

type Props = {
  onClose: () => void,
  onSubmit: (token: string, values: Object) => Promise<void>,
  title: string,
  submitLabel: string,
  initialValues?: Object,
  showLicenseField?: boolean,
};

type State = {
  cardComplete: boolean,
  expiryComplete: boolean,
  cvcComplete: boolean,
  submitting: boolean,
};

class BillingFormInner extends PureComponent<Props, State> {
  state = {
    cardComplete: false,
    expiryComplete: false,
    cvcComplete: false,
    submitting: false,
  };

  cardRef: any = null;
  expiryRef: any = null;
  cvcRef: any = null;
  stripeRef: any = null;
  elementsRef: any = null;

  handleSubmit = async (values: Object) => {
    const { onSubmit, onClose } = this.props;

    if (!this.stripeRef || !this.elementsRef) {
      Actions.alertNotification('Stripe is not loaded yet.', 'error');
      return;
    }

    this.setState({ submitting: true });

    try {
      const cardElement = this.elementsRef.getElement(CardNumberElement);
      const { token, error } = await this.stripeRef.createToken(cardElement, {
        name: values.fullName,
        address_line1: values.addressLine1,
        address_line2: values.addressLine2 || '',
        address_city: values.city,
        address_state: values.state,
        address_zip: values.zip,
        address_country: values.country,
      });

      if (error) {
        Actions.alertNotification(error.message, 'error');
        this.setState({ submitting: false });
        return;
      }

      await onSubmit(token.id, values);
      onClose();
    } catch (e) {
      Actions.alertNotification(
        (e && e.message) || 'An error occurred processing your card.',
        'error',
      );
      this.setState({ submitting: false });
    }
  };

  render() {
    const { onClose, title, submitLabel, initialValues, showLicenseField } = this.props;
    const { cardComplete, expiryComplete, cvcComplete, submitting } = this.state;
    const stripeFieldsComplete = cardComplete && expiryComplete && cvcComplete;

    return (
      <Modal maxWidth={600}>
        <Formik
          initialValues={{
            fullName: (initialValues && initialValues.fullName) || '',
            addressLine1: (initialValues && initialValues.addressLine1) || '',
            addressLine2: (initialValues && initialValues.addressLine2) || '',
            city: (initialValues && initialValues.city) || '',
            state: (initialValues && initialValues.state) || '',
            zip: (initialValues && initialValues.zip) || '',
            country: (initialValues && initialValues.country) || 'US',
            userLicenses: (initialValues && initialValues.userLicenses) || 1,
          }}
          validationSchema={validationSchema}
          onSubmit={this.handleSubmit}
          render={({
            values,
            isValid,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form>
              <ModalHeader header={title} onClose={onClose} />
              <ModalBody withPadding>
                <StripeRefCapture formRef={this} />
                <FieldGroupRow
                  left={
                    <FieldGroup
                      name="fullName"
                      label="Name on Card"
                      placeholder="Full Name"
                    />
                  }
                  right={
                    showLicenseField ? (
                      <FieldGroup
                        name="userLicenses"
                        label="User Licenses"
                        type="number"
                      />
                    ) : null
                  }
                />

                <StripeField>
                  <FieldLabel>Card Number</FieldLabel>
                  <StripeFieldWrapper>
                    <CardNumberElement
                      options={{ style: CARD_ELEMENT_STYLE }}
                      onChange={(e) => this.setState({ cardComplete: e.complete })}
                    />
                  </StripeFieldWrapper>
                </StripeField>

                <FieldGroupRow
                  left={
                    <StripeField>
                      <FieldLabel>Expiry</FieldLabel>
                      <StripeFieldWrapper>
                        <CardExpiryElement
                          options={{ style: CARD_ELEMENT_STYLE }}
                          onChange={(e) => this.setState({ expiryComplete: e.complete })}
                        />
                      </StripeFieldWrapper>
                    </StripeField>
                  }
                  right={
                    <StripeField>
                      <FieldLabel>CVC</FieldLabel>
                      <StripeFieldWrapper>
                        <CardCvcElement
                          options={{ style: CARD_ELEMENT_STYLE }}
                          onChange={(e) => this.setState({ cvcComplete: e.complete })}
                        />
                      </StripeFieldWrapper>
                    </StripeField>
                  }
                />

                <FieldGroupRow
                  left={
                    <AddressAutocomplete
                      value={values.addressLine1 || ''}
                      label="Address Line 1"
                      placeholder="123 Main St"
                      onChange={val => setFieldValue('addressLine1', val)}
                      onAddressSelect={({ line1, city, state, zip }) => {
                        if (line1) setFieldValue('addressLine1', line1);
                        if (city) setFieldValue('city', city);
                        if (state) setFieldValue('state', state);
                        if (zip) setFieldValue('zip', zip);
                      }}
                    />
                  }
                  right={
                    <FieldGroup
                      name="addressLine2"
                      label="Address Line 2"
                      placeholder="Suite / Apt"
                    />
                  }
                />

                <FieldGroupRow
                  left={
                    <FieldGroup name="city" label="City" placeholder="City" />
                  }
                  center={
                    <FieldGroup name="state" label="State" placeholder="State" />
                  }
                  right={
                    <FieldGroup name="zip" label="Zip" placeholder="Zip" />
                  }
                />

                <FieldGroupRow
                  left={
                    <FieldGroup name="country" label="Country" placeholder="US" />
                  }
                />
              </ModalBody>
              <ModalFooter>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!isValid || !stripeFieldsComplete || submitting}
                  loading={submitting}
                  theme="green"
                >
                  {submitLabel}
                </Button>
              </ModalFooter>
            </Form>
          )}
        />
      </Modal>
    );
  }
}

/**
 * Small hook wrapper to grab stripe/elements refs and pass them up.
 */
function StripeRefCapture({ formRef }: Object) {
  const StripeHooks = require('@stripe/react-stripe-js');
  const stripe = StripeHooks.useStripe();
  const elements = StripeHooks.useElements();

  React.useEffect(() => {
    if (stripe && elements && formRef) {
      formRef.stripeRef = stripe;
      formRef.elementsRef = elements;
    }
  }, [stripe, elements, formRef]);

  return null;
}

/**
 * Wrapped in Stripe Elements provider.
 */
export default function BillingForm(props: Props) {
  const stripePromise = getStripe();

  if (!stripePromise) {
    return null;
  }

  return (
    <Elements stripe={stripePromise}>
      <BillingFormInner {...props} />
    </Elements>
  );
}
